/**
 * Как редактор называет отказ сервера переводчику: какая реплика (по времени, как она видна на
 * дорожке) и почему — словами сервера. Показывается в красной полосе над дорожками
 * (SubtitlesBlock), той же, что у склейки и разбивки перевода.
 */
import { EditMassTranslatedSubtitleResponse } from "../../api/fastApi/types";
import { ILang, ISubtitlesDataAll } from "./types";
import { cuesRu, pluralRu } from "../../utils/pluralRu";

const pad = (n: number, width = 2) => String(n).padStart(width, "0");

/** Короче сервер реплику оригинала не примет (проверка времени: конец − начало ≥ 100 мс). */
export const MIN_CUE_MS = 100;

/**
 * Почему это время реплики нельзя отправлять — или null. Реплика оригинала: начало не раньше
 * папки и длина не меньше 0,1 с (иначе сервер откажет, и правка навсегда повиснет
 * несохранённой). Озвучка: только место — начало не раньше папки; её длина — длина файла, а
 * начало позже русского конца — законная раскладка.
 */
export const timingRefusal = (isSource: boolean, start: number, length: number): string | null => {
    const finite = (n: number) => typeof n === "number" && Number.isFinite(n);
    if (!finite(start) || start < 0) {
        return isSource ? "начало реплики раньше начала папки" : "озвучка поставлена раньше начала папки";
    }
    if (isSource) {
        if (!finite(length) || length <= 0) return "конец реплики не позже её начала";
        if (length < MIN_CUE_MS) return "реплика короче 0,1 с";
    }
    return null;
};

/** 4159 → «0:04», 3 723 000 → «1:02:03». Нечисло — «?»: время могло и испортиться. */
export const formatCueTime = (ms: number | null | undefined): string => {
    if (typeof ms !== "number" || !Number.isFinite(ms)) return "?";
    const total = Math.floor(Math.abs(ms) / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const body = h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
    return ms < 0 ? `−${body}` : body;
};

/** 4159 → «0:04.159»: окно новой реплики называется до миллисекунды, как его увидит сервер. */
export const formatWindowTime = (ms: number): string => {
    const whole = Math.max(0, Math.round(ms));
    const total = Math.floor(whole / 1000);
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    const clock = h ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
    return `${clock}.${pad(whole % 1000, 3)}`;
};

/** Начало реплики в первой из пар языков, где она есть (строки дорожки идут по оригиналу). */
export const cueStartMs = (
    data: Partial<ISubtitlesDataAll>,
    langs: ILang[],
    folder: number,
    id: string,
): number | undefined => {
    for (const lang of langs) {
        const found = data[lang]?.[folder - 1]?.translations?.find((t) => t.id === id);
        if (found) return found.start_rus_milli ?? found.start;
    }
    return undefined;
};

/**
 * Какие реплики пакета mass_edit сервер НЕ записал: `results[i].error` (старый сервер отвечает
 * 200 и прячет отказ туда) и список `failed` (новый, 207). id — из эха запроса, иначе по
 * порядку: записи results идут в порядке тела.
 */
export const massEditFailures = (
    sentIds: string[],
    response: EditMassTranslatedSubtitleResponse | undefined,
): Map<string, string> => {
    const failures = new Map<string, string>();
    const results: any[] = Array.isArray(response?.results) ? (response!.results as any[]) : [];
    results.forEach((item, i) => {
        if (item && typeof item.error === "string" && item.error) {
            const id = String(item.data?.id ?? sentIds[i] ?? "");
            if (id) failures.set(id, item.error);
        }
    });
    (Array.isArray(response?.failed) ? response!.failed : []).forEach((id) => {
        if (!failures.has(String(id))) failures.set(String(id), "сервер не записал реплику");
    });
    return failures;
};

export interface CueFailure {
    id: string;
    atMs?: number;
    reason: string;
}

const describe = ({ id, atMs, reason }: CueFailure) =>
    `${atMs !== undefined ? formatCueTime(atMs) : id} — ${reason}`;

/** «Не сохранено: реплика 0:04 — <причина>»; несколько — первые три и счёт остальных. */
export const saveFailureMessage = (failures: CueFailure[]): string => {
    if (failures.length === 1) return `Не сохранено: реплика ${describe(failures[0])}`;
    const shown = failures.slice(0, 3).map(describe).join("; ");
    const more = failures.length > 3 ? ` (и ещё ${failures.length - 3})` : "";
    const n = failures.length;
    return `${pluralRu(n, "Не сохранена", "Не сохранены", "Не сохранено")} ${cuesRu(n)}: ${shown}${more}`;
};

/**
 * Что автосохранение не записало: ключ несохранённой правки (`<язык>:<id>`) → причина. `transient`
 * — не ответил весь запрос (сеть, 5xx) или пакет сорвался посреди: это не отказ по реплике, и
 * выводов о ней («нет пары») из него делать нельзя.
 */
export interface SaveRefusal {
    refused: Map<string, string>;
    transient: boolean;
}

/** Ошибка автосохранения с тем, что именно не записано (её бросает flushGate). */
export const saveRefusedError = (message: string, refusal: SaveRefusal): Error =>
    Object.assign(new Error(message), { saveRefusal: refusal });

/** Что не записано — если ошибка пришла от автосохранения; иначе null. */
export const saveRefusalOf = (error: unknown): SaveRefusal | null =>
    error && typeof error === "object" && "saveRefusal" in error
        ? ((error as { saveRefusal: SaveRefusal }).saveRefusal ?? null)
        : null;
